import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { font } from '../../styles/typography';
import { useIncomingCalls } from './IncomingCallProvider';
import { useAuth } from '../../modules/auth/context/AuthContext';
import { formatCallerNumber, formatLine, formatDateShort, userName } from '../../modules/telephony/utils/callFormat';
import { getNumberTimeline } from '../../modules/telephony/telephonyService';
import { formatRecordsCount } from '../../modules/telephony/utils/recordsLabel';

const MODE_COLOR = {
  ringing: 'var(--spark-color-warning-text)',
  talking: 'var(--spark-color-success-text)',
  ended: 'var(--spark-color-text-muted)',
};

const Card = styled.div`
  background: var(--spark-color-surface);
  border: 1px solid ${(p) => MODE_COLOR[p.$mode] || 'var(--spark-color-control-border)'};
  border-left: 5px solid ${(p) => MODE_COLOR[p.$mode] || 'var(--spark-color-control-disabled-text)'};
  border-radius: 10px;
  box-shadow: 0 8px 24px var(--spark-color-shadow-strong);
  padding: 12px 14px;
  color: var(--spark-color-text);
  animation: slide-in 160ms ease-out;
  @keyframes slide-in {
    from { transform: translateY(-8px); opacity: 0; }
    to { transform: none; opacity: 1; }
  }
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const Line = styled.div`
  ${font('sm')}
  color: var(--spark-color-text-secondary);
`;

const Number = styled.div`
  ${font('xl')}
  font-weight: 700;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
`;

const State = styled.div`
  ${font('sm')}
  color: ${(p) => MODE_COLOR[p.$mode] || 'var(--spark-color-text-muted)'};
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const CloseBtn = styled.button`
  border: 0;
  background: transparent;
  color: var(--spark-color-text-muted);
  cursor: pointer;
  ${font('lg')}
  line-height: 1;
  padding: 0 2px;
  &:hover { color: var(--spark-color-text-control); }
`;

const Who = styled.div`
  margin-top: 8px;
  ${font('base')}
  div + div { margin-top: 2px; }
`;

const Muted = styled.div`
  ${font('md')}
  color: var(--spark-color-text-secondary);
`;

const History = styled.ul`
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  ${font('md')}
  color: var(--spark-color-text-secondary);
  li { display: flex; gap: 6px; padding: 2px 0; }
  li span:first-child { color: var(--spark-color-text-muted); white-space: nowrap; }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  ${font('base')}
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid var(--spark-color-action-primary);
  background: ${(p) => (p.$primary ? 'var(--spark-color-action-primary)' : 'var(--spark-color-surface)')};
  color: ${(p) => (p.$primary ? 'var(--spark-color-on-action-primary)' : 'var(--spark-color-action-primary)')};
  &:hover { background: ${(p) => (p.$primary ? 'var(--spark-color-action-primary-hover)' : 'var(--spark-color-control-hover-surface)')}; }
`;

function fmtDuration(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const CALL_LABEL = { COMPLETED: 'розмова', MISSED: 'пропущений', ANSWERED: 'розмова', RINGING: 'дзвінок' };
const MAX_MATCHES = 3;

/**
 * Компактна плашка вхідного дзвінка. Не перекидає оператора на сторінку сама
 * (щоб не втратити незбережені дані) — перехід кнопкою.
 */
export default function IncomingCallToast({ call, onOpen }) {
  const { dismiss } = useIncomingCalls();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [now, setNow] = useState(() => Date.now());
  // кількість записів історії номера — з правами самого користувача (не з SSE-пейлоаду)
  const [timelineCount, setTimelineCount] = useState(0);
  const callerNumberRaw = call.callerNumberRaw;
  useEffect(() => {
    if (!callerNumberRaw) { setTimelineCount(0); return undefined; }
    let cancelled = false;
    getNumberTimeline({ phone: callerNumberRaw })
      .then((res) => { if (!cancelled) setTimelineCount(res?.count || 0); })
      .catch(() => { if (!cancelled) setTimelineCount(0); });
    return () => { cancelled = true; };
  }, [callerNumberRaw]);

  const mine = call.answeredUserId && call.answeredUserId === user?.id;
  const ringing = call.status === 'RINGING';
  const active = call.status === 'ANSWERED' && mine;
  const answeredElsewhere = call.status === 'ANSWERED' && !mine;
  const mode = ringing ? 'ringing' : active ? 'talking' : 'ended';

  useEffect(() => {
    if (!active && !ringing) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active, ringing]);

  const duration = call.answeredAt
    ? fmtDuration((active ? now : new Date(call.endedAt || now).getTime()) - new Date(call.answeredAt).getTime())
    : ringing
      ? fmtDuration(now - new Date(call.startedAt).getTime())
      : null;

  const stateLabel = ringing
    ? '☎ Дзвонить'
    : active
      ? '● Розмова'
      : answeredElsewhere
        ? 'Відповів інший'
        : call.status === 'MISSED'
          ? 'Пропущений'
          : 'Завершено';

  const match = call.match || {};
  const persons = match.contactPersons || [];
  const counterparties = (match.counterparties || []).filter((c) => !persons.some((p) => p.counterparties?.some((pc) => pc.id === c.id)));
  const staffLine = match.phoneLine;
  const cities = match.cities || [];
  const history = (match.history?.calls || []).slice(0, 3);
  const callPath = `/telephony/calls/${call.callId}`;
  const onCallPage = location.pathname === callPath;

  return (
    <Card $mode={mode} role="status">
      <Head>
        <div>
          <Line>→ {formatLine(call.line, call.did)}</Line>
          <Number>{formatCallerNumber(call.callerNumberRaw)}</Number>
          {call.callerName && <Muted>{call.callerName}</Muted>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <State $mode={mode}>{stateLabel}{duration ? ` · ${duration}` : ''}</State>
          <CloseBtn type="button" onClick={() => dismiss(call.callId)} aria-label="Закрити">×</CloseBtn>
        </div>
      </Head>

      <Who>
        {persons.slice(0, MAX_MATCHES).map((p) => (
          <div key={p.id}>
            <b>{p.name}</b>
            {p.counterparties?.length > 0 && <Muted>{p.counterparties.map((c) => c.name).join(', ')}</Muted>}
          </div>
        ))}
        {counterparties.slice(0, MAX_MATCHES).map((c) => (
          <div key={c.id}>
            <b>{c.name}</b>
            {c.mainManager && <Muted>менеджер: {userName(c.mainManager)}</Muted>}
          </div>
        ))}
        {persons.length + counterparties.length > MAX_MATCHES && (
          <Muted>+{persons.length + counterparties.length - MAX_MATCHES} ще збігів</Muted>
        )}
        {staffLine && (
          <div>
            <b>Співробітник: {staffLine.user ? userName(staffLine.user) : staffLine.employeeName || 'корпоративний номер'}</b>
            {staffLine.department && <Muted>{staffLine.department.name}</Muted>}
          </div>
        )}
        {persons.length === 0 && counterparties.length === 0 && !staffLine && (
          <Muted>Невідомий номер — у довідниках не знайдено</Muted>
        )}
        {cities.length > 0 && <Muted>Місто: {cities.join(', ')}</Muted>}
      </Who>

      {timelineCount > 0 && <Muted>Історія: {formatRecordsCount(timelineCount)}</Muted>}

      {history.length > 0 && (
        <History>
          {history.map((h) => (
            <li key={h.id}>
              <span>{formatDateShort(h.startedAt)}</span>
              <span>
                {CALL_LABEL[h.status] || h.status}
                {h.inquiry ? ` · ${h.inquiry.subject}${h.inquiry.counterparty ? ` (${h.inquiry.counterparty.name})` : ''}${h.inquiry.assignedTo ? ` → ${userName(h.inquiry.assignedTo)}` : ''}` : ''}
              </span>
            </li>
          ))}
        </History>
      )}

      <Actions>
        {!onCallPage && !answeredElsewhere && (
          <Btn type="button" $primary onClick={() => { navigate(callPath); onOpen?.(); }}>
            {ringing ? 'Відкрити дзвінок' : call.inquiry ? 'Відкрити дзвінок' : 'Відкрити · зареєструвати звернення'}
          </Btn>
        )}
        <Btn type="button" onClick={() => dismiss(call.callId)}>Закрити</Btn>
      </Actions>
    </Card>
  );
}
